<template>
  <fieldset
    class="BankAccountNumber"
    ref="componentRef"
  >
    <legend>{{ t('hiring.common.bank-info.account') }}</legend>
    <div class="grid-inputs">
      <v-text-field
        ref="$prefix"
        :id="`${id}-prefix`"
        :aria-label="`${t('hiring.common.bank-info.account')} - IBAN`"
        v-model="localPrefixFormatted"
        @keydown="(event: KeyboardEvent) => acceptCharacters(event, ALLOW_CHARS)"
        placeholder="XXXX"
        @paste="handlePaste"
        :rules="prefixValidations"
        :hide-details="true"
        :error="!!fieldValidationErrorMessage"
        :maxlength="4"
      />

      <v-text-field
        ref="$main"
        :id="`${id}-main`"
        :aria-label="`${t('hiring.common.bank-info.account')} - Número`"
        v-model="localMainFormatted"
        @keydown="(event: KeyboardEvent) => acceptCharacters(event, ALLOW_CHARS)"
        placeholder="XXXX XXXX XXXX XXXX XXXX"
        :rules="mainValidations"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
      />
    </div>

    <div
      :id="`${id}-prefix-messages`"
      hidden
    ></div>
    <div
      :id="`${id}-main-messages`"
      hidden
    ></div>

    <div aria-hidden="true">
      <v-text-field
        ref="$iban"
        :id="`${id}-full-iban`"
        class="hidden"
        v-model="localIban"
        :rules="ibanValidations"
        :hide-details="true"
      />
    </div>

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </fieldset>
</template>

<script setup lang="ts">
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { IBAN_SIZES } from '@/modules/core/constants/common'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
  import ErrorMessageComponent from './ErrorMessageComponent.vue'

  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { acceptCharacters } from '@/utils/input.utils'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const ALLOW_CHARS = /[\w\d]/ // [\w\d-]
  const emit = defineEmits(['update:modelValue'])

  interface PropsInterface {
    id: string
    modelValue: string | null
    rules?: ValidationRuleType[]
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    modelValue: null,
    rules: () => []
  })

  const $prefix = ref()
  const $main = ref()
  const $iban = ref()

  const componentRef = ref<HTMLElement | null>(null)
  let observer: MutationObserver | null = null

  onMounted(() => {
    observer = startDuplicatedLabelControl(componentRef.value)
  })

  onUnmounted(() => {
    endDuplicatedLabelControl(observer)
  })

  const prefix = ref(props.modelValue?.slice(0, 4) ?? '')
  const main = ref(props.modelValue?.slice(4) ?? '')

  const validationRules = {
    required: (value: string) => !!value || t('common.validations.required'),
    equal4: (value: string) => (value && value.length === 4) || t('common.validations.format'),
    validIBAN: () => {
      const fullIban = localIban.value.toUpperCase()
      const countryCode = fullIban.substring(0, 2)

      const allowedLetterCountries = ['IE', 'LI', 'MT', 'NL', 'GB', 'IT']

      if (!(countryCode in IBAN_SIZES)) {
        return t('common.validations.format')
      }

      const expectedLength = IBAN_SIZES[countryCode as keyof typeof IBAN_SIZES]

      if (allowedLetterCountries.includes(countryCode)) {
        if (!/^[A-Z\d]+$/.test(fullIban.substring(4))) {
          return t('common.validations.format', { countryCode, length: expectedLength })
        }
      } else {
        if (!/^\d+$/.test(fullIban.substring(4))) {
          return t('common.validations.format', { countryCode, length: expectedLength })
        }
      }

      return (
        !expectedLength ||
        fullIban.length === expectedLength ||
        t('common.validations.format', { countryCode, length: expectedLength })
      )
    }
  }

  const prefixValidations = [validationRules.required, validationRules.equal4]
  const mainValidations = [validationRules.required]
  const ibanValidations = [validationRules.validIBAN, ...props.rules]

  const localPrefixFormatted = computed({
    get() {
      return prefix.value.toUpperCase()
    },
    set(value: string) {
      prefix.value = value.toUpperCase()
    }
  })
  const localMainFormatted = computed({
    get() {
      return addIBANFormat(main.value)
    },
    set(newValue: string) {
      main.value = addIBANFormat(newValue)
    }
  })
  const localIban = computed(() => prefix.value + main.value.replace(/\s+/g, ''))

  const fieldValidationErrorMessage = computed(() => {
    if (!$prefix?.value || !$main?.value) return ''

    return (
      $prefix.value?.errorMessages[0] ||
      $main.value?.errorMessages[0] ||
      $iban.value?.errorMessages[0] ||
      ''
    )
  })

  watch([prefix, main], () => {
    emit('update:modelValue', localIban.value)
  })

  function addIBANFormat(iban: string) {
    const rawIban = iban.replace(/\s+/g, '')

    let formattedIban = ''
    for (let i = 0; i < rawIban.length; i += 4) {
      if (i > 0) formattedIban += ' '
      formattedIban += rawIban.substring(i, i + 4)
    }

    return formattedIban.toUpperCase()
  }

  function handlePaste(event: ClipboardEvent) {
    event.preventDefault()
    const pastedData = event.clipboardData?.getData('text') || ''
    prefix.value = pastedData.slice(0, 4).toUpperCase()
    main.value = pastedData.slice(4).replace(/\s+/g, '').toUpperCase()
  }
</script>

<style scoped lang="scss">
  .BankAccountNumber {
    border: none;

    legend {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-s;
    }

    .grid-inputs {
      display: grid;
      align-items: flex-start;
      grid-template-columns: 4.5rem 1fr;
      gap: $gap-l;

      :deep(.v-field) {
        background-color: $color-grey-200;
        border-radius: $b-radius-0;
        box-shadow: $box-shadow-0;
        transition: all 0.2s ease-in-out;

        .v-field__prepend-inner {
          i::before {
            font-family: $font-family-icons;
          }
        }

        .v-field__input {
          font-family: $font-family-primary;
          color: $color-neutral-dark;

          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }

        &.v-field--disabled {
          opacity: 1;
        }

        &.v-field--error {
          background-color: $color-error-200;

          .v-field__input {
            color: $color-error;

            &::placeholder {
              color: $color-neutral;
              opacity: 1;
            }
          }
        }
      }
    }

    .hidden {
      display: none;
    }
  }
</style>
